import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledInput } from 'src/components/ui/Input/Input';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import LoadingSkeleton from 'src/components/ui/LoadingSkeleton/LoadingSkeleton';
import { toast } from 'react-toastify';
import { $publicApi } from 'src/services/api';
import logger from 'src/utils/logger';
import { extractBackendErrorMessage } from '../errorUtils';
import { REGISTRATION_DOCUMENT_TYPES, VerificationFormValues, verificationSchema } from '../schema';
import { useRegistrationFormContext } from '../RegistrationFormContext';
import RegistrationDocumentUploader from '../RegistrationDocumentUploader';
import RegistrationFormErrors from '../RegistrationFormErrors';

interface VerificationStepProps {
  registrationKey: string;
  onAdvance: () => void;
  onBack: () => void;
}

const VerificationStep: React.FC<VerificationStepProps> = ({ registrationKey, onAdvance, onBack }) => {
  const { t } = useTranslation();
  const { formState, setFormState } = useRegistrationFormContext();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationFormValues>({
    resolver: zodResolver(verificationSchema),
    defaultValues: formState.verification,
    mode: 'onSubmit',
  });

  const { mutateAsync, isPending } = $publicApi.useMutation('put', '/registrations/{registration_key}');

  const documentsQuery = $publicApi.useQuery('get', '/registrations/{registration_key}/documents/', {
    params: { path: { registration_key: registrationKey } },
  });

  const documents = documentsQuery.data ?? [];
  const logoDocs = documents.filter(d => d.document_type === REGISTRATION_DOCUMENT_TYPES.LOGO);
  const idDocs = documents.filter(d => d.document_type === REGISTRATION_DOCUMENT_TYPES.ID_DOCUMENT);

  const onSubmit = async (values: VerificationFormValues) => {
    if (logoDocs.length === 0 || idDocs.length === 0) {
      return;
    }
    try {
      await mutateAsync({
        params: { path: { registration_key: registrationKey } },
        body: {
          partner_name: values.partner_name,
          partner_position: values.partner_position,
          partner_email: values.partner_email,
          partner_phone: values.partner_phone,
        },
      });
      setFormState(prev => ({ ...prev, verification: values }));
      onAdvance();
    } catch (err: unknown) {
      logger.error('Failed to save verification data', err);
      const backendMessage = extractBackendErrorMessage(err);
      toast.error(backendMessage || t('registration.errors.save_failed'));
    }
  };

  const canAdvance = logoDocs.length > 0 && idDocs.length > 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-24">
      <h3 className="typo-title-24 text-full">{t('registration.steps.verification')}</h3>

      <RegistrationFormErrors errors={errors} />

      <div>
        <h4 className="typo-title-20 text-full mb-12">{t('registration.fields.partner_section')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <ControlledInput
            control={control}
            name="partner_name"
            label={t('registration.fields.partner_name')}
            placeholder=""
          />
          <ControlledInput
            control={control}
            name="partner_position"
            label={t('registration.fields.partner_position')}
            placeholder=""
          />
          <ControlledInput
            control={control}
            name="partner_email"
            label={t('registration.fields.partner_email')}
            placeholder={t('registration.placeholders.email')}
            type="email"
          />
          <ControlledInput
            control={control}
            name="partner_phone"
            label={t('registration.fields.partner_phone')}
            placeholder={t('registration.placeholders.phone')}
          />
        </div>
      </div>

      {documentsQuery.isLoading ? (
        <LoadingSkeleton numberOfItems={2} />
      ) : (
        <div className="flex flex-col gap-16">
          <RegistrationDocumentUploader
            registrationKey={registrationKey}
            documentType={REGISTRATION_DOCUMENT_TYPES.LOGO}
            documents={logoDocs}
            totalDocumentCount={documents.length}
          />
          <RegistrationDocumentUploader
            registrationKey={registrationKey}
            documentType={REGISTRATION_DOCUMENT_TYPES.ID_DOCUMENT}
            documents={idDocs}
            totalDocumentCount={documents.length}
          />
        </div>
      )}

      {!canAdvance && <p className="typo-text-14 status-error-full">{t('registration.documents.missing_required')}</p>}

      <div className="flex justify-between pt-16">
        <Button title={t('registration.back')} tone={ButtonToneProps.SECONDARY} onClick={onBack} />
        <Button
          title={t('registration.next')}
          tone={ButtonToneProps.PRIMARY_BLUE}
          type="submit"
          disabled={isPending || !canAdvance}
        />
      </div>
    </form>
  );
};

export default VerificationStep;
